/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import {
  ControlledInput,
  ControlledTextarea,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useUploadImage } from "../../store/useUploadImage";
import { useAuth } from "../../store/useAuth";
import { useAlert } from "../../store/useAlert";
import { IPartner, usePartners } from "../../store/usePartners";
import { Switch } from "@material-tailwind/react";

type Inputs = {
  name: string;
  en_description: string;
  nl_description: string;
  website: string;
};

function AddPartnerScreen() {
  /** Hooks */
  const { addPartner, addedPartner, clearAddedPartner } = usePartners();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const { uploadImage, clearUploadedImage, uploadedImage, thumbnailImage } =
    useUploadImage();
  const { setAlert } = useAlert();
  const currentUser = useAuth((state) => state.user);
  const canSetCourier = [1, 7].includes(Number(currentUser?.user_role ?? 0));
  // Opened from the Carrier partners page. A carrier is created there and
  // nowhere else, so the ordinary form no longer asks the question at all.
  const [searchParams] = useSearchParams();
  const isCarrierForm = canSetCourier && searchParams.get("carrier") === "1";
  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [showInApp, setShowInApp] = useState<boolean>(false);
  const isCourier = isCarrierForm;

  /** Effects */
  useEffect(() => {
    if (addedPartner) {
      clearAddedPartner();
      clearUploadedImage();
      navigate(-1);
    }
  }, [addedPartner]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const onSubmit = async (data: Inputs) => {
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    let body: IPartner = {
      ...data,
      image: uploadedImage,
      // A carrier starts hidden from the app catalogue; an admin can show it
      // later from its edit page. Sent explicitly so the payload agrees with
      // the backend, which creates every carrier hidden.
      showInApp: isCourier ? false : showInApp,
    };
    // Only sent by a role allowed to set it — see canSetCourier.
    if (canSetCourier) {
      body.isCourier = isCourier;
    }
    if (thumbnailImage) {
      body.thumbnail = thumbnailImage;
    }
    addPartner(body);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image, true);
  };

  const imageError = submitted && !image;

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new partner
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white rounded-xl border-gray-border drop-shadow-none mb-2 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New partner
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              placeholder="Partner name"
              name="name"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.website)}
              label="Website"
              placeholder="Partner website"
              name="website"
              rules={{
                required: true,
                pattern: /https?:\/\/.+/,
              }}
            />
            <ControlledTextarea
              name="en_description"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.en_description)}
              label="Description (EN)"
              placeholder="Type partner description in English language here"
              inputClassName="min-h-[180px]"
            />
            <ControlledTextarea
              name="nl_description"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.nl_description)}
              label="Description (NL)"
              placeholder="Type partner description in Dutch language here"
              inputClassName="min-h-[180px]"
            />
            <FormUpload
              label="Partner logo"
              onChange={onImageChange}
              value={image}
              error={imageError}
            />
            {isCarrierForm ? (
              <p className="text-xs text-gray-600">
                This is a carrier partner. It starts hidden from the app's
                partner list; an admin can show it later from its edit page.
                The app offers carriers when a customer registers a parcel, and
                a parcel challenge picks its carriers from them.
              </p>
            ) : (
              <Switch
                crossOrigin={"anonymous"}
                color="teal"
                ripple
                label="Show this partner in mobile app"
                checked={showInApp}
                onChange={(e) => setShowInApp(e?.target.checked)}
              />
            )}
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(AddPartnerScreen);
